import { isRecord } from "@arti/utilities";

import type { ProviderProgressEntry } from "../../../hooks/useProviderProgress";
import type { BudgetSeverity, PhaseStatusData } from "./types";

function isBudgetSeverity(value: unknown): value is BudgetSeverity {
  return (
    value === "ok" ||
    value === "warning" ||
    value === "critical" ||
    value === "exceeded"
  );
}

/** Extract the latest phase status from events for a specific stream */
export function extractLatestPhaseStatus(
  events: ProviderProgressEntry[],
  streamId: string,
): PhaseStatusData | null {
  // Events are in chronological order (oldest first), so iterate backwards to find the latest
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i];
    if (event.event_type === "phase:status" && event.stream_id === streamId) {
      const data = event.event_data;
      const phase = typeof data.phase === "string" ? data.phase : null;
      if (!phase) {
        continue;
      }

      // Extract budget if present
      let budget: PhaseStatusData["budget"] = null;
      if (isRecord(data.budget)) {
        const b = data.budget;
        if (
          typeof b.usedInputTokens === "number" &&
          typeof b.maxInputTokens === "number" &&
          typeof b.percentUsed === "number" &&
          isBudgetSeverity(b.severity)
        ) {
          budget = {
            averageBurnRateInputTokensPerStep:
              typeof b.averageBurnRateInputTokensPerStep === "number"
                ? b.averageBurnRateInputTokensPerStep
                : null,
            deductedInputTokens:
              typeof b.deductedInputTokens === "number"
                ? b.deductedInputTokens
                : 0,
            estimatedRemainingSteps:
              typeof b.estimatedRemainingSteps === "number"
                ? b.estimatedRemainingSteps
                : null,
            maxInputTokens: b.maxInputTokens,
            percentUsed: b.percentUsed,
            severity: b.severity,
            usedInputTokens: b.usedInputTokens,
          };
        }
      }

      // Extract context window size if present
      const contextWindowSize =
        typeof data.contextWindowSize === "number"
          ? data.contextWindowSize
          : undefined;

      return { budget, contextWindowSize, phase };
    }
  }
  return null;
}
